import "./AccountPerformanceViewComponent.scss";

import {IComponentOptions, IOnDestroy, IOnInit, IScope} from "angular";
import {AccountPerformanceViewStore} from "../service/AccountPerformanceViewStore";
import {Observable, ReplaySubject} from "rxjs";
import {CurrentUserService} from "../../services/user/CurrentUserService";
import {StateService} from "@uirouter/core";
import {PerformanceStateParams} from "../models/PerformanceStateParams";
import {AccountPerformanceSummary} from "../models/AccountPerformanceSummary";
import {AccountStore} from "../../account/store/AccountStore";
import {Branding} from "../../types/Branding";
import {PerformanceChartData} from "../models/PerformanceChart";
import {Pair, PairOf} from "../../types/GenericTypes";
import {IsoDate} from "../../pseudo-types/DateTypes";
import {Optional} from "../../js/util/Optional";
import {DateTimeUtil} from "../../js/util/DateTimeUtil";
import moment from "moment";
import {AsxGameService} from "../../game/service/AsxGameService";

export const AccountPerformanceView = "accountPerformanceView";

export function accountPerformanceView(): IComponentOptions {
    return {
        templateUrl: "app/performance/view/AccountPerformanceViewComponent.html",
        bindings: {
            viewStore: "<"
        },
        controllerAs: "ctrl",
        controller: AccountPerformanceViewComponentController
    };
}

export class AccountPerformanceViewComponentController implements IOnInit, IOnDestroy {

    public viewStore: AccountPerformanceViewStore;
    public summary$: Observable<AccountPerformanceSummary>;
    public showKeyIndicators = false;
    public showAccountPerformanceChart = false;

    public readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private $scope: IScope,
                       private $state: StateService,
                       private $stateParams: PerformanceStateParams,
                       private accountStore: AccountStore,
                       private currentUserService: CurrentUserService,
                       private asxGameService: AsxGameService) {
    }

    public $onInit() {
        this.summary$ = this.viewStore
                            .accountPerformance
                            .takeUntil(this.destroyed$);
        Observable.combineLatest(this.currentUserService.current, this.viewStore.chartData)
            .takeUntil(this.destroyed$)
            .do(([user, data]) => {
                if (!user.isBranding(Branding.GAME)) {
                    this.showAccountPerformanceChart = true;
                    this.showKeyIndicators = user.isSuperUser() || user.branding() !== Branding.IV;
                } else {
                    this.updateChartDataRange(data);
                }
            })
            .do(() => this.$scope.$applyAsync())
            .subscribe();

        const portfolioId = +this.$stateParams.portfolioId;
        this.accountStore.selectAccount(portfolioId);
    }

    private updateChartDataRange(data: PerformanceChartData) {
        this.asxGameService.dates().then(dates => {
            if (data.length !== 0) {
                const startIndex = data.findIndex(chartData => new Date(chartData.asAt) >= new Date(dates.opens));
                if (startIndex !== null && startIndex !== undefined) {
                    const range = this.dataDateRange(data, [startIndex, data.length - 1]);
                    this.viewStore.updateDataRange(range);
                }
            }
        });
    }

    private dataDateRange(data: PerformanceChartData, indexRange: Pair<number>): Pair<IsoDate> {
        const start = Optional.of(indexRange)
                              .map((range) => range[0])
                              .map(Math.floor)
                              .map((d) => Math.max(d, 0))
                              .orElse(0);

        const maxIndex = data.length - 1;

        const end = Optional.of(indexRange)
                            .map((range) => range[1])
                            .map(Math.ceil)
                            .map((i) => Math.min(i, maxIndex))
                            .orElse(maxIndex);

        const dataStart = data[start];
        const dataEnd = data[end];

        const isoStart = DateTimeUtil.momentToIsoDate(moment(dataStart.asAt));
        const isoEnd = DateTimeUtil.momentToIsoDate(moment(dataEnd.asAt));

        return PairOf(isoStart, isoEnd);
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
